import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Gauge } from "lucide-react";

function ScoreBar({ label, value, color }) {
  return (
    <div data-testid={`score-bar-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        <span className="text-xs font-mono font-bold">{value ?? 0}%</span>
      </div>
      <div className="h-2.5 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${color}`}
          style={{ width: `${value ?? 0}%` }}
        />
      </div>
    </div>
  );
}

function getOverallColor(score) {
  if (score >= 75) return "text-green-700";
  if (score >= 50) return "text-yellow-700";
  return "text-red-700";
}

export default function ConfidenceBreakdown({ breakdown }) {
  if (!breakdown) return null;

  const overall = breakdown.overall ?? 0;

  return (
    <Card className="border border-border shadow-none" data-testid="confidence-breakdown-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-bold flex items-center gap-2">
            <Gauge className="w-5 h-5 text-muted-foreground" />
            Confidence Breakdown
          </CardTitle>
          <Badge variant="outline" className={`font-mono text-sm ${getOverallColor(overall)}`}>
            {overall}/100
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <ScoreBar
            label="Lexical Overlap"
            value={breakdown.lexical_overlap}
            color="bg-blue-500"
          />
          <ScoreBar
            label="Semantic Similarity"
            value={breakdown.semantic_similarity}
            color="bg-violet-500"
          />
          <ScoreBar
            label="Grounding Strength"
            value={breakdown.grounding_strength}
            color="bg-emerald-500"
          />
        </div>

        {/* Visual summary */}
        <div className="mt-4 pt-4 border-t border-border">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="p-2 rounded-sm bg-blue-50">
              <div className="text-lg font-bold font-mono text-blue-700">
                {breakdown.lexical_overlap ?? 0}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Lexical</div>
            </div>
            <div className="p-2 rounded-sm bg-violet-50">
              <div className="text-lg font-bold font-mono text-violet-700">
                {breakdown.semantic_similarity ?? 0}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Semantic</div>
            </div>
            <div className="p-2 rounded-sm bg-emerald-50">
              <div className="text-lg font-bold font-mono text-emerald-700">
                {breakdown.grounding_strength ?? 0}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Grounding</div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
